//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { useTranslation } from 'react-i18next';
import { UnifiedCardData } from './UnifiedCardData';

interface RelatedLinksProps {
  card: UnifiedCardData;
  primaryLabel?: string;
}

const renderUrlGroup = (
  labelKey: string,
  urls: string[] | undefined,
  t: (k: string) => string,
) => {
  if (!Array.isArray(urls) || urls.length === 0) return null;
  return urls.filter(Boolean).map((u, idx) => (
    <div
      className="mb-3 border-l-2 border-[#0043ce] pl-3 dark:border-brand-interactiveDark"
      id="id-related-links-url-group"
      data-testid="related-links-url-group"
      key={`${labelKey}-${idx}`}
    >
      <div
        className="cursor-pointer text-[12px] font-medium text-[#0043ce] hover:underline dark:text-brand-interactiveDark"
        id="id-related-links-url-label"
        data-testid="related-links-url-label"
      >
        {urls.length > 1 ? `${t(labelKey)} #${idx + 1}` : t(labelKey)}
      </div>
      <a
        href={u}
        target="_blank"
        rel="noopener noreferrer"
        className="break-all text-[11px] text-gray-600 no-underline dark:text-gray-300"
      >
        {u}
      </a>
    </div>
  ));
};

export const RelatedLinks: React.FC<RelatedLinksProps> = ({
  card,
  primaryLabel = 'primary-link',
}) => {
  const { t } = useTranslation();

  const hasAny =
    !!card.url ||
    (card.buildUrls?.length || 0) > 0 ||
    (card.commitUrls?.length || 0) > 0 ||
    (card.pullRequestUrls?.length || 0) > 0;
  if (!hasAny) return null;

  return (
    <div
      className="mt-4 space-y-3"
      id="id-related-links-container"
      data-testid="related-links-container"
    >
      <span
        className="text-[12px] font-semibold text-gray-700 dark:text-gray-300"
        id="id-related-links-title"
        data-testid="related-links-title"
      >
        {t('links-related-result')}
      </span>
      <div id="id-related-links-list" data-testid="related-links-list">
        {/* Primary URL */}
        {card.url && (
          <div
            className="mb-3 border-l-2 border-[#0043ce] pl-3 dark:border-brand-interactiveDark"
            id="id-related-links-primary-url"
            data-testid="related-links-primary-url"
          >
            <div
              className="cursor-pointer text-[12px] font-medium text-[#0043ce] hover:underline dark:text-brand-interactiveDark"
              id="id-related-links-primary-label"
              data-testid="related-links-primary-label"
            >
              {t(primaryLabel)}
            </div>
            <a
              href={card.url}
              target="_blank"
              rel="noopener noreferrer"
              className="break-all text-[11px] text-gray-600 no-underline dark:text-gray-300"
            >
              {card.url}
            </a>
          </div>
        )}
        {/* Build URLs */}
        {renderUrlGroup('build-url', card.buildUrls, t)}
        {/* Commit URLs */}
        {renderUrlGroup('commit-url', card.commitUrls, t)}
        {/* Pull Request URLs */}
        {renderUrlGroup('pull-request-url', card.pullRequestUrls, t)}
      </div>
    </div>
  );
};

export default RelatedLinks;
